import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { Img, WorksPageHeader } from "../../components/site/Common";
import { lazyImgs } from "../../lib/html";
import { ShareButtons } from "../../components/site/Cards";
import { Loading } from "./NotFound";
import { Briefcase } from "lucide-react";

function ServiceIcon({ name, size = 32, className = "" }) {
  const Cmp = (name && LucideIcons[name]) || Briefcase;
  return <Cmp size={size} className={className} />;
}

// Deterministic gradient per service slug (used when no cover image)
function slugGradient(slug = "") {
  const palettes = [
    ["#7c3aed", "#ec4899", "#f59e0b"], // purple → pink → amber
    ["#059669", "#14b8a6", "#06b6d4"], // emerald → teal → cyan
    ["#dc2626", "#f97316", "#eab308"], // red → orange → yellow
    ["#2563eb", "#8b5cf6", "#d946ef"], // blue → violet → fuchsia
    ["#0ea5e9", "#6366f1", "#a855f7"], // sky → indigo → purple
    ["#f59e0b", "#ef4444", "#ec4899"], // amber → red → pink
  ];
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  const p = palettes[hash % palettes.length];
  return `linear-gradient(135deg, ${p[0]} 0%, ${p[1]} 55%, ${p[2]} 100%)`;
}

function ServiceCard({ s, t, lang }) {
  const hasImage = !!s.cover;
  const cardTitle = t(s.card_title) || t(s.title);
  return (
    <Link
      to={`/calismalarim/${s.slug}`}
      className="svc-card group relative flex flex-col h-full overflow-hidden rounded-2xl p-2 transition-all duration-500 hover:-translate-y-1"
      data-testid={`service-card-${s.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-xl">
        {hasImage ? (
          <Img
            src={s.cover}
            alt={cardTitle}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full relative flex items-center justify-center transition-transform duration-700 group-hover:scale-[1.03]"
            style={{ background: slugGradient(s.slug) }}
            aria-hidden
          >
            <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_50%)]" aria-hidden />
            <span className="absolute inset-0 bg-[radial-gradient(circle_at_80%_90%,rgba(0,0,0,0.25),transparent_60%)]" aria-hidden />
            <ServiceIcon name={s.icon} size={56} className="relative text-white/90 drop-shadow-xl" />
          </div>
        )}
      </div>

      <div className="px-4 pt-4 pb-4">
        <h3 className="text-[1.05rem] sm:text-[1.1rem] font-semibold leading-snug tracking-tight truncate" style={{ fontFamily: "var(--s-body)", textTransform: "none", letterSpacing: 0 }}>
          {cardTitle}
        </h3>
      </div>
    </Link>
  );
}

export default function Works({ page, lang, t }) {
  const { site } = useSite();
  const { data, isLoading } = usePublic(["public-services"], "/public/services");
  if (isLoading) return <Loading />;
  const items = data?.items || [];
  const strip = site?.services_strip || {};
  const logos = (strip.logos || []).filter((l) => l.image);
  const showStrip = strip.enabled && logos.length > 0;
  const profile = site?.profile || {};

  return (
    <article data-testid="page-view" className="pb-28 sm:pb-40 relative overflow-hidden">
      <span className="pointer-events-none absolute top-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#d4af370c] blur-[150px]" aria-hidden />
      <span className="pointer-events-none absolute top-96 -right-32 w-[460px] h-[460px] rounded-full bg-[#d4af3708] blur-[160px]" aria-hidden />

      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 relative">
        <WorksPageHeader
          eyebrow={lang === "tr" ? "Hizmetler ve çalışmalar" : "Services & work"}
          title={t(page.title)}
          share={<ShareButtons title={t(page.title)} testid="works-share-button" kind="page" shareUrl={`${window.location.origin}/api/public/share/page/${page.slug}?v=${encodeURIComponent((page.updated_at || "").replace(/[^0-9]/g, "").slice(0, 12) || "1")}`} />}
        />
        {page.body && (
          <div className="mt-8 text-base sm:text-lg leading-relaxed text-white/60 max-w-3xl" dangerouslySetInnerHTML={{ __html: lazyImgs(t(page.body)) }} />
        )}
      </header>

      {showStrip && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-16 relative" data-testid="services-logo-strip">
          <p className="eyebrow text-center mb-6">{t(strip.title) || (lang === "tr" ? "Birlikte çalıştığım kurumlar" : "Trusted by")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-7 opacity-80">
            {logos.map((l, i) => {
              const Node = (
                <img src={l.image} alt={l.name || "logo"} className="h-10 sm:h-12 w-auto object-contain grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all" loading="lazy" />
              );
              return l.url ? (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" title={l.name}>{Node}</a>
              ) : (
                <span key={i} title={l.name}>{Node}</span>
              );
            })}
          </div>
        </section>
      )}

      {items.length === 0 ? (
        <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-16 text-center text-sm text-white/50" data-testid="services-empty">
          {lang === "tr" ? "Hizmetler yakında eklenecek." : "Services coming soon."}
        </div>
      ) : (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-16 sm:mt-20 relative">
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7" data-testid="services-grid">
            {items.map((s, i) => (
              <li key={s.id} className="h-full"><ServiceCard s={s} t={t} lang={lang} /></li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
